import React, { Suspense } from 'react';
import AuthGatedPage from './AuthGatedPage';
import RouteFallback from './RouteFallback';
import ReportsPage from '../pages/ReportsPage';
import { PageKey, NavPage } from '../utils/routes';
import { useCreditInfo } from '../hooks/useCreditInfo';
import { T } from '../designTokens';

const AdminPolicyAnalyzerPage = React.lazy(() => import('../pages/AdminPolicyAnalyzerPage'));
const AdminUsersPage = React.lazy(() => import('../pages/AdminUsersPage'));
const AdminUserDetailPage = React.lazy(() => import('../pages/AdminUserDetailPage'));
const QueuePage = React.lazy(() => import('../pages/QueuePage'));
const BillingPage = React.lazy(() => import('../pages/BillingPage'));
const MonitoringPage = React.lazy(() => import('../pages/MonitoringPage'));

interface SecondaryPagesRouteProps {
  currentPage: PageKey;
  /** Only relevant for the 'admin-user-detail' page; App.tsx falls through to
   * the scanner homepage when this route is selected without an id (the same
   * guard the six-branch chain this replaces used). */
  adminUserId: string | null;
  navigateTo: (page: NavPage) => void;
  queueCount: number;
  authEnabled: boolean;
  creditInfo: ReturnType<typeof useCreditInfo>;
  onBillingClick: () => void;
  isSignedIn: boolean;
  isAdmin?: boolean;
  getToken?: () => Promise<string | null>;
  setHashRoute: (route: string) => void;
}

// The six AuthGatedPage-wrapped routes (#/queue, #/billing, #/reports, and
// the three /admin/* routes) — extracted verbatim out of App()'s sequence of
// `if (currentPage === ...)` branches (issue #416). Returns null when
// `currentPage` doesn't match one of these six (including 'admin-user-detail'
// with no `adminUserId`), which App.tsx treats as "fall through to the
// scanner homepage", matching the original inline behavior.
//
// Named (lowercase-first) and called directly as a plain function rather
// than as JSX (`renderSecondaryPagesRoute(props)`, not
// `<SecondaryPagesRoute />`) — App.tsx needs the actual `ReactElement | null`
// return value to decide whether to fall through, and a JSX element's mere
// existence doesn't tell you what the component underneath eventually
// renders. It uses no hooks, so calling it directly is safe.
export function renderSecondaryPagesRoute({
  currentPage,
  adminUserId,
  navigateTo,
  queueCount,
  authEnabled,
  creditInfo,
  onBillingClick,
  isSignedIn,
  isAdmin,
  getToken,
  setHashRoute,
}: SecondaryPagesRouteProps): React.ReactElement | null {
  if (currentPage === 'queue') {
    return (
      <AuthGatedPage
        headerPage="queue"
        navigateTo={navigateTo}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditInfo={creditInfo}
        onBillingClick={onBillingClick}
        isSignedIn={isSignedIn}
        authWallTitle="Sign in to view your scan queue"
        authWallMessage="Your scan queue is private and scoped to your account. Sign in to view and manage it."
      >
        <Suspense fallback={<RouteFallback />}>
          <QueuePage onBack={() => setHashRoute('#/scanner')} />
        </Suspense>
      </AuthGatedPage>
    );
  }

  if (currentPage === 'billing') {
    return (
      <AuthGatedPage
        headerPage="billing"
        navigateTo={navigateTo}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditInfo={creditInfo}
        onBillingClick={onBillingClick}
        backgroundColor={T.gray900}
        isSignedIn={isSignedIn}
        authWallTitle="Sign in to view your billing"
        authWallMessage="Your credit balance and purchase history are private to your account. Sign in to view them."
      >
        <Suspense fallback={<RouteFallback />}>
          <BillingPage
            onViewReport={(reportId) => setHashRoute(`#/report/${encodeURIComponent(reportId)}`)}
          />
        </Suspense>
      </AuthGatedPage>
    );
  }

  if (currentPage === 'monitoring') {
    return (
      <AuthGatedPage
        headerPage="monitoring"
        navigateTo={navigateTo}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditInfo={creditInfo}
        onBillingClick={onBillingClick}
        backgroundColor={T.gray900}
        isSignedIn={isSignedIn}
        authWallTitle="Sign in to view your monitored sites"
        authWallMessage="Your monitoring schedule is private to your account. Sign in to view and manage it."
      >
        <Suspense fallback={<RouteFallback />}>
          <MonitoringPage
            onViewReport={(reportId) => setHashRoute(`#/report/${encodeURIComponent(reportId)}`)}
          />
        </Suspense>
      </AuthGatedPage>
    );
  }

  if (currentPage === 'reports') {
    return (
      <AuthGatedPage
        headerPage="reports"
        navigateTo={navigateTo}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditInfo={creditInfo}
        onBillingClick={onBillingClick}
        isSignedIn={isSignedIn}
        authWallTitle="Sign in to view your reports"
        authWallMessage="Your reports are private and scoped to your account. Sign in to access them."
      >
        <ReportsPage
          inProgressCount={queueCount}
          onBack={() => setHashRoute('#/scanner')}
          onViewScan={(reportId) => setHashRoute(`#/report/${encodeURIComponent(reportId)}`)}
          onViewPolicyAnalyzerAdmin={() => setHashRoute('#/admin/policy-analyzer')}
          getToken={getToken}
          isAdmin={isAdmin}
        />
      </AuthGatedPage>
    );
  }

  if (currentPage === 'admin-policy-analyzer') {
    return (
      <AuthGatedPage
        headerPage="reports"
        navigateTo={navigateTo}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditInfo={creditInfo}
        onBillingClick={onBillingClick}
        isSignedIn={isSignedIn}
        authWallTitle="Sign in to view admin policy history"
        authWallMessage="Admin history is restricted to authenticated admin accounts."
        requireAdmin
        isAdmin={isAdmin}
        adminWallMessage="You need admin privileges to view policy analyzer request history."
      >
        <Suspense fallback={<RouteFallback />}>
          <AdminPolicyAnalyzerPage
            onBack={() => setHashRoute('#/reports')}
            getToken={getToken}
          />
        </Suspense>
      </AuthGatedPage>
    );
  }

  if (currentPage === 'admin-users') {
    return (
      <AuthGatedPage
        headerPage="reports"
        navigateTo={navigateTo}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditInfo={creditInfo}
        onBillingClick={onBillingClick}
        isSignedIn={isSignedIn}
        authWallTitle="Sign in to view admin users"
        authWallMessage="Admin views are restricted to authenticated admin accounts."
        requireAdmin
        isAdmin={isAdmin}
        adminWallMessage="You need admin privileges to view user management."
      >
        <Suspense fallback={<RouteFallback />}>
          <AdminUsersPage
            onBack={() => setHashRoute('#/reports')}
            onViewUser={(id) => setHashRoute(`#/admin/users/${encodeURIComponent(id)}`)}
            getToken={getToken}
          />
        </Suspense>
      </AuthGatedPage>
    );
  }

  if (currentPage === 'admin-user-detail' && adminUserId) {
    return (
      <AuthGatedPage
        headerPage="reports"
        navigateTo={navigateTo}
        queueCount={queueCount}
        authEnabled={authEnabled}
        creditInfo={creditInfo}
        onBillingClick={onBillingClick}
        isSignedIn={isSignedIn}
        authWallTitle="Sign in to view user details"
        authWallMessage="Admin views are restricted to authenticated admin accounts."
        requireAdmin
        isAdmin={isAdmin}
        adminWallMessage="You need admin privileges to view user management."
      >
        <Suspense fallback={<RouteFallback />}>
          <AdminUserDetailPage
            userId={adminUserId}
            onBack={() => setHashRoute('#/admin/users')}
            onViewScan={(id) => setHashRoute(`#/report/${encodeURIComponent(id)}`)}
            getToken={getToken}
          />
        </Suspense>
      </AuthGatedPage>
    );
  }

  return null;
}
